<template>
  <div class="custom-input-group">
    <v-select
      :items="selectItems"
      v-model="selectValue"
      solo
      flat
      dense
      hide-details
      class="select-input"
      label="Select"
      :style="{ width: '150px' }"
    />
    <v-autocomplete
      v-model="inputValue"
      :items="[...(recentSearches[selectValue] || []), ...searchSuggestions]"
      :search-input.sync="searchValue"
      solo
      flat
      dense
      hide-details
      class="text-input"
      :style="{ width: '400px' }"
      append-icon=""
      label="Type here for search..."
      placeholder="Type here for search..."
      @update:search-input="debouncedFetchSuggestions"
      @change="handleSelection"
      :return-object="true"
    >
      <template v-slot:item="data">
        <div class="flex justify-between text-sm">
          <span>{{ data.item.text }}</span>
          <v-icon
            small
            class="ml-2"
            @click.stop="removeSearchTerm(data.item)"
            v-if="recentSearches[selectValue]?.includes(data.item)"
          >
            mdi-close-circle
          </v-icon>
        </div>
      </template>
    </v-autocomplete>
  </div>
</template>

<script>
import debounce from "lodash.debounce";
export default {
  data() {
    return {
      subCases: this.$store.state.subCases,
      selectValue: null,
      inputValue: "",
      searchValue: "",
      selectItems: [
        { text: "Employees", value: "EMPLOYEES" },
        { text: "Teams", value: "TEAMS" },
      ],
      searchSuggestions: [],
      debouncedFetchSuggestions: null,
      recentSearches: this.getRecentSearches(),
    };
  },
  watch: {
    selectValue() {
      this.inputValue = "";
      this.searchValue = "";
      this.searchSuggestions = [];
    },
    $route(to) {
      if (!Object.keys(to.query).length) {
        this.inputValue = "";
      }
    },
    "$store.state.subCases": {
      handler(newSubCases) {
        this.subCases = newSubCases;
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    fetchSuggestions() {
      if (!this.searchValue) return;
      if (this.selectValue === "EMPLOYEES") {
        this.fetchEmployeeSuggestions();
      } else if (this.selectValue === "TEAMS") {
        this.fetchTeamSuggestions();
      }
    },

    async fetchEmployeeSuggestions() {
      try {
        const params = { search: this.searchValue };
        const response = await this.$axios.get("workforce/v2/users", {
          params,
        });

        this.searchSuggestions = response.data.users.map((user) => {
          return {
            text: `${user.fullName} - Reporting To: ${
              user.reportesTo?.fullName || "No-One"
            }`,
            value: user.firstName,
          };
        });
      } catch (error) {
        console.error("Error fetching employee suggestions:", error);
      }
    },
    async fetchTeamSuggestions() {
      try {
        const params = { search: this.searchValue };
        const response = await this.$axios.get("workforce/collection/cases", {
          params: params,
        });
        this.searchSuggestions = response.data.cases.map((el) => {
          return {
            text: el.customer
              ? `${el.caseNo} - ${el.customer?.customerFullName} - ${el.customer?.crn}`
              : el.caseNo,
            value: { cNo: el.caseNo, sCName: el.caseType },
          };
        });
      } catch (error) {
        console.error("Error fetching team suggestions:", error);
      }
    },

    handleSelection(selectedItem) {
      if (!selectedItem) return;
      const { text, value } = selectedItem;

      if (this.selectValue === "EMPLOYEES" && text) {
        this.$router.push({
          path: "/workforce/employees",
          query: { search: value },
        });
      } else if (this.selectValue === "TEAMS" && text) {
        const fC = this.subCases[0]?.subCase?.find(
          (el) => el.name === value?.sCName
        );
        if (fC?._id) {
          this.$router.push({
            path: `/workforce/collection-management/${fC?._id}`,
            query: { search: value?.cNo },
          });
        } else {
          this.$toast.error("Please check the case number");
        }
      }
      this.saveSearchTerm(selectedItem);
    },
    saveSearchTerm(term) {
      if (typeof window !== "undefined") {
        let searches = this.getRecentSearches();

        if (!searches[this.selectValue]) {
          searches[this.selectValue] = [];
        }
        if (this.selectValue === "TEAMS") {
          const existingIndex = searches[this.selectValue].findIndex(
            (item) => item.value.cNo === term.value.cNo
          );

          if (existingIndex !== -1) {
            searches[this.selectValue].splice(existingIndex, 1);
          }
        } else {
          const existingIndex = searches[this.selectValue].findIndex(
            (item) => item.value === term.value
          );

          if (existingIndex !== -1) {
            searches[this.selectValue].splice(existingIndex, 1);
          }
        }
        searches[this.selectValue].unshift(term);
        if (searches[this.selectValue].length > 5) {
          searches[this.selectValue].pop();
        }
        localStorage.setItem("recentSearches", JSON.stringify(searches));
        this.recentSearches = searches;
      }
    },
    getRecentSearches() {
      if (typeof window !== "undefined") {
        return JSON.parse(localStorage.getItem("recentSearches")) || {};
      }
      return {};
    },
    removeSearchTerm(item) {
      let searches = this.getRecentSearches();

      if (searches[this.selectValue]) {
        if (this.selectValue === "TEAMS") {
          searches[this.selectValue] = searches[this.selectValue].filter(
            (search) => search.value.cNo !== item.value.cNo
          );
        } else {
          searches[this.selectValue] = searches[this.selectValue].filter(
            (search) => search.value !== item.value
          );
        }
        localStorage.setItem("recentSearches", JSON.stringify(searches));
        this.recentSearches = searches;
      }
    },
  },
  created() {
    this.debouncedFetchSuggestions = debounce(this.fetchSuggestions, 500);
  },
  mounted() {
    this.selectValue = this.selectItems[0].value;
  },
};
</script>

<style scoped>
.custom-input-group {
  display: flex;
  align-items: center;
  padding: 2px;
  background-color: white;
}

.custom-input-group .v-select .v-input__control,
.custom-input-group .v-autocomplete .v-input__control {
  background-color: white !important;
}

.select-input {
  flex-grow: 1;
  width: 15%;
  font-size: smaller;
  border-right: 1px solid #ccc;
  border-top-right-radius: 0px;
  border-bottom-right-radius: 0px;
}

.text-input {
  flex-grow: 2;
  margin-left: 2px;
  width: 90%;
  border-top-left-radius: 0px;
  border-bottom-left-radius: 0px;
}
</style>
